import { ApiError } from "@wetalk/api-client";

import { apiErrorText } from "../../../api/api-error.js";
import { usePortalMessage } from "../../../i18n/message.js";
import { portalText, usePortalTranslation, portalLocale } from "../../../i18n/portal.js";
/**
 * `data-screen-label="Agent"`, tab `versions` — the paid optimisation review (P14-07).
 *
 * WHERE THIS BELONGS, AND HOW THAT WAS ESTABLISHED. The prototype does **not** put the paid
 * pass on the Review tab. In `design/WeTalk.dc.html` the Review tab's conditional block runs
 * from line 1750 to 1819 and holds exactly two panes, `rev.isGaps` and `rev.isFlagged` —
 * which is P11-03, already built in `review-tab.tsx`. The depth chooser, the price, the
 * findings with their per-finding switch and the "Publish v8 with N fixes" button are all
 * inside the Versions tab's block (lines 2092–2225), bound to the script block's
 * `ai` object (line 5630), sitting between the test-set card and the version history. That
 * is where a paid pass belongs: the thing it produces is an `agent_version`, and the screen
 * that already explains what publishing one does to conversations in flight is this one.
 * P11-02 reserved the spot explicitly — `versions-tab.tsx` carried a placeholder panel whose
 * own words were "is built by P14-07 … It is named below rather than mocked." This replaces
 * that placeholder.
 *
 * NOTHING IS ACCEPTED BY DEFAULT, AND THAT IS A DEPARTURE FROM THE PROTOTYPE. The design's
 * `ai.findings` pre-switches every finding on, so "Publish v8 with 4 fixes" is one click away
 * from a screen nobody has read. What gets published is a **new live version of a customer's
 * agent**, so the sandbox starts every finding `pending` and this screen renders three
 * decisions — undecided, accepted, rejected — rather than a two-state switch. Publishing is
 * disabled while nothing is accepted, because a version carrying no fixes is not a version
 * anybody asked for.
 *
 * ULTRA READS THE AUDIO, AND THE SCREEN SAYS SO BEFORE THE MONEY MOVES. The Ultra depth runs
 * on the frontier provider (Anthropic) behind the provider wall, because the shared GPU pool
 * is text-only and cannot hear a pause. The consequence a customer actually meets is not the
 * wall, it is their own retention policy: recordings deleted after seven days mean there is
 * no audio to listen to, and `OptimisationReviewQuoteView` says that as a **named reason**
 * rather than as a greyed-out button. So the Ultra card renders its reason in words, the
 * recording count sits beside the conversation count, and the run button refuses rather than
 * quietly selling a review of nothing.
 *
 * PUBLISHING IS A COMPARE-AND-SET, NOT A WRITE. `publishOptimisationReview` carries the
 * `live_version_seq` this screen last read, and the server runs
 * `UPDATE … WHERE live_version_seq = $expected`. Losing that race is not an error to retry:
 * retrying with the same number is asking to overwrite a decision nobody here has seen. On a
 * conflict this panel says what happened, refetches the agent so the next attempt quotes the
 * sequence that is actually live, and leaves every finding decision exactly as it was.
 *
 * AMENDED 2026-10-09 (plan-015 DRF, gap G9, ADR 0043 — owner answer 3): the accepted fixes make a DRAFT — the
 * design's "Create draft vN with N fixes". It answers no calls until someone who may publish today publishes it
 * from the version history above; the review then reads "In a draft" (its version never published) and, once
 * published, "Published". The review's charge is unchanged — it was taken when the review ran. The design's
 * "The draft runs the test set first" is not said: running the test set is not built ("soon" on the test-set
 * card), so only its second sentence is — "Nothing changes on live calls until you publish it."
 *
 * WHAT THE DESIGN ASKS FOR THAT IS NOT DRAWN, AND WHY.
 *
 *   · **Per-finding glyphs.** `OptimisationFindingView.iconName` carries Phosphor names
 *     (`ph-duotone ph-clock`, `ph-duotone ph-translate`, …) that `src/shell/glyph.ts` does
 *     not hold. That registry raises on an unknown name rather than substituting a
 *     look-alike, which is the behaviour to keep; adding the entries is a change in a file
 *     this unit does not own. So every finding is drawn with one glyph and the field is left
 *     unread rather than mistranslated.
 *   · AMENDED 2026-09-25 (customer-portal review #37, #95, #119; fix round P): the offer is not
 *     drawn at all until there are calls to review — an agent with no calls in the window, or no
 *     live version (the Versions tab leaves the panel out), is not sold a review of nothing. A
 *     reason that concerns the whole window or the whole account (no calls, not enough credit) is
 *     said ONCE, under the cards — it was printed on each card and again below, three times — and
 *     the credit notice says that reviews are charged to credit, not to free call minutes, with the
 *     account's credit and the price (`optimisationReviewCreditNoticeOf`).
 *   · **The review is not in the URL.** Every other piece of state on this surface is a
 *     typed search parameter, but the search schema is validated in `src/router.tsx`, which
 *     this unit does not own. A started review therefore lives in component state; it is
 *     reachable again through the API, not through a link.
 *
 * AMENDED 2026-10-09 (design sync): two steps before money moves. The banner offers "Review calls";
 * pressing it shows "Choose how deep it goes" — the depth cards — with "Start review · €2.40" (or
 * "Start ultra review · …"), Cancel and "Taken from your credit when it starts." The banner's line
 * says nothing changes on live calls until the owner approves.
 *
 * AMENDED 2026-10-28 (plan-032 WAPP-OR, design §43.11; owner AP-0 "build with defaults"; copy drafted until AP-1 draws
 * it in Claude Design). The factory runs a started review now, so this panel shows every state the engine has:
 *
 *   · **Restored after a reload.** The newest review comes back from `listOptimisationReviews` (the review is still not
 *     in the URL, but it is no longer lost): one waiting or running shows its progress, one done with findings shows
 *     them until "Put this aside", and one that failed or found nothing says so above the offer.
 *   · **Off (OD-6).** While the api marks every option `not_available_yet`, the offer is drawn with its button disabled
 *     and "Coming soon". A template that is never reviewed (`not_offered` on every option) is offered nothing.
 *   · **Ultra (OD-1).** Not offered in the first release: its card is marked "Coming later" and cannot be chosen.
 *   · **Waiting and running.** "Waiting to start" with the amount held from the credit (DD32-6), then "Reading up to N
 *     conversations" — no percentage and no cycling progress lines: the run's steps are not reported, so none is drawn.
 *   · **Done.** "Read N of M conversations"; findings by position, each with "N of M conversations", its fix and
 *     Accept/Reject; "Show the conversations" opens its evidence lines (or says they are withheld or gone); an advice
 *     finding carries "Change it yourself" and the screen to do it on (Edit, Questions, Channels) and no Accept.
 *     A review that found nothing: "Nothing to change in these conversations — no charge" (OD-2).
 *   · **Failed.** The reason's sentence (design §43.7.2) and "Nothing was charged."
 *   · **The start note** is now "Held from your credit while it runs. Charged only when it finishes with something to
 *     change." — the price is held from the start and charged only by a review that found something.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Button, Field, Icon, Tag, Textarea } from "@wetalk/ui";
import { useEffect, useRef, useState, type ReactNode } from "react";

import { GLYPH } from "../../../shell/glyph.js";
import { useGateway } from "../../../api/provider.js";
import {
  QUERY_KEY,
  agentVersionListQuery,
  authSessionQuery,
  creditBalanceQuery,
  optimisationReviewQuery,
  optimisationReviewQuoteQuery,
} from "../../../api/query.js";
import {
  DEPTH_COPY,
  isDepthOwnReason,
  isOptimisationReviewComingSoon,
  isOptimisationReviewNotOffered,
  isUsableAbsorbedReason,
  optimisationAdviceScreenOf,
  optimisationFailureText,
  optimisationReviewCreditNoticeOf,
  reviewedCountText,
  unavailableReasonText,
  type OptimisationAdviceScreenView,
} from "../../../api/optimisation-review-view.js";
import { formatMoney } from "../../../money.js";
import type {
  AgentVersionSummaryView,
  ConsoleGateway,
  OptimisationFindingDecisionView,
  OptimisationFindingEvidenceLineView,
  OptimisationFindingView,
  OptimisationReviewDepthView,
  OptimisationReviewPayerView,
  OptimisationReviewQuoteView,
  OptimisationReviewScopeView,
  OptimisationReviewView,
  OptimisationReviewCreditView,
} from "../../../api/gateway.js";

/**
 * The window a customer's review reads: the last seven days — the prototype's "Reading 1,284
 * transcripts". The design draws no window chooser on this tab (the three-way choice is the
 * operator console's, `adm.opt.scopes`), so the customer's pass is the seven-day one and the
 * run button's `title` says how many conversations that is.
 */
const OPTIMISATION_REVIEW_SCOPE: OptimisationReviewScopeView = "last_7d";

/**
 * How often a waiting or running review is re-read. It finishes in the factory, in minutes, not here (plan-032: was one
 * second while nothing ran it).
 */
const POLL_MS = 3_000;

/** plan-032: the VoiceAgent's reviews, newest first — the first page restores the newest after a reload. */
const optimisationReviewListQuery = (gateway: ConsoleGateway, voiceAgentId: string) =>
  ({
    queryKey: [...QUERY_KEY.optimisationReview, "list", voiceAgentId],
    queryFn: () => gateway.listOptimisationReviews(voiceAgentId, null),
  }) as const;

/** What the last publish attempt did, in the words the customer needs. */
interface PublishOutcome {
  readonly kind: "published" | "conflict";
  readonly label: string;
  readonly appliedCount: number;
}

/**
 * Which review the panel shows: `follow` — the newest from the list (restored after a reload); or one `chosen` — the one
 * just started, or none after "Put this aside".
 */
type Shown = { readonly kind: "follow" } | { readonly kind: "chosen"; readonly optimisationReviewId: string | null };

export interface OptimisationReviewPanelProps {
  readonly voiceAgentId: string;
  /**
   * The sequence the Versions tab last read, quoted back on publish.
   *
   * A prop rather than a second read of the same query, so the number this panel sends and
   * the number the version list is drawn from cannot disagree with each other.
   */
  readonly liveVersionSeq: number;
  /** "Review by hand" (design `editAgent`): opens the Edit panel on the live version. */
  readonly onEditByHand?: () => void;
  /** plan-032: an advice finding about the transfer number — "Change it yourself" opens Channels. */
  readonly onOpenChannelBindingTab?: () => void;
  /** plan-032: an advice finding about a survey's questions — "Change it yourself" opens Questions (survey agents). */
  readonly onOpenQuestions?: () => void;
}

export function OptimisationReviewPanel(props: OptimisationReviewPanelProps): ReactNode {
  usePortalTranslation("operations");
  const gateway = useGateway();
  const queryClient = useQueryClient();

  const scope = OPTIMISATION_REVIEW_SCOPE;
  const [depth, setDepth] = useState<OptimisationReviewDepthView>("standard");
  // WHO PAYS IS THE SESSION'S, NOT A CHOICE. The API answers `payer = 'wetalk'` from a
  // person with 403, and an operator acting as a member may not charge the account
  // (`billing.write` is on the impersonation denial list). So a member's review is charged
  // to the account, an operator's is absorbed by WeTalk with a recorded reason, and the
  // screen offers neither the other way round.
  const authSession = useQuery(authSessionQuery(gateway));
  const payer: OptimisationReviewPayerView =
    authSession.data?.actor.kind === "operator" ? "wetalk" : "customer";
  const [absorbedReason, setAbsorbedReason] = useState("");
  const [shown, setShown] = useState<Shown>({ kind: "follow" });
  const [outcome, setOutcome] = useState<PublishOutcome | null>(null);
  const [refusal, setRefusal] = usePortalMessage();

  const quote = useQuery(optimisationReviewQuoteQuery(gateway, props.voiceAgentId, scope));
  // Only for the credit notice's figures. A role that cannot read billing gets the shorter,
  // still true sentence — never a guessed balance.
  const credit = useQuery({ ...creditBalanceQuery(gateway), retry: false });
  const optimisationReviewPage = useQuery({ ...optimisationReviewListQuery(gateway, props.voiceAgentId), retry: false });

  const optimisationReviewId =
    shown.kind === "chosen" ? shown.optimisationReviewId : (optimisationReviewPage.data?.row[0]?.optimisationReviewId ?? null);

  const current = useQuery({
    ...optimisationReviewQuery(gateway, optimisationReviewId ?? "none"),
    enabled: optimisationReviewId !== null,
    refetchInterval: (query) =>
      query.state.data?.state === "running" || query.state.data?.state === "idle"
        ? POLL_MS
        : false,
  });

  // When a review this screen watched finishes, the hold ends (DD32-6): the quote, the balance and the list are read
  // again. A ref, not state: what the review was before is bookkeeping, never drawn.
  const watchedState = useRef<string | null>(null);
  const currentState = current.data?.state ?? null;
  useEffect(() => {
    const before = watchedState.current;
    watchedState.current = currentState;
    if ((before === "idle" || before === "running") && (currentState === "done" || currentState === "failed")) {
      void queryClient.invalidateQueries({ queryKey: [...QUERY_KEY.optimisationReview, "quote"] });
      void queryClient.invalidateQueries({ queryKey: [...QUERY_KEY.optimisationReview, "list"] });
      void queryClient.invalidateQueries({ queryKey: QUERY_KEY.creditBalance });
    }
  }, [currentState, queryClient]);

  const start = useMutation({
    mutationFn: () =>
      gateway.startOptimisationReview({
        voiceAgentId: props.voiceAgentId,
        depth,
        scope,
        payer,
        absorbedReason: payer === "wetalk" ? absorbedReason.trim() : null,
      }),
    async onSuccess(made) {
      setRefusal(null);
      setOutcome(null);
      setShown({ kind: "chosen", optimisationReviewId: made.optimisationReviewId });
      queryClient.setQueryData(
        [...QUERY_KEY.optimisationReview, made.optimisationReviewId],
        made,
      );
      // The price is held from now (DD32-6): the quote's credit and the list change.
      await queryClient.invalidateQueries({ queryKey: [...QUERY_KEY.optimisationReview, "quote"] });
      await queryClient.invalidateQueries({ queryKey: [...QUERY_KEY.optimisationReview, "list"] });
    },
    async onError(error: Error) {
      // The gateway refuses an absorbed review with no usable reason. Say what it said
      // rather than swallowing it into an empty screen.
      setRefusal(() => (apiErrorText(error, "")));
      // plan-032: somebody else started one first — show theirs, the newest, instead of the offer.
      if (error instanceof ApiError && error.code === "optimisation_review_already_open") {
        setShown({ kind: "follow" });
        await queryClient.invalidateQueries({ queryKey: [...QUERY_KEY.optimisationReview, "list"] });
      }
    },
  });

  const decide = useMutation({
    mutationFn: (intent: {
      readonly optimisationFindingId: string;
      readonly decision: OptimisationFindingDecisionView;
    }) => {
      if (optimisationReviewId === null) {
        throw new Error(portalText("operations", "no_optimisation_review_is_open_so_there_is_no_finding_to_d_02413d50"));
      }
      return gateway.decideOptimisationFinding(
        optimisationReviewId,
        intent.optimisationFindingId,
        intent.decision,
      );
    },
    onSuccess(next) {
      queryClient.setQueryData(
        [...QUERY_KEY.optimisationReview, next.optimisationReviewId],
        next,
      );
    },
  });

  const publish = useMutation({
    mutationFn: () => {
      if (optimisationReviewId === null) {
        throw new Error(portalText("operations", "no_optimisation_review_is_open_so_there_is_nothing_to_publ_a16ec564"));
      }
      return gateway.publishOptimisationReview(optimisationReviewId, props.liveVersionSeq);
    },
    async onSuccess(published) {
      setOutcome({
        kind: "published",
        label: current.data?.nextAgentVersionLabel ?? portalText("operations", "the_draft_469b439f"),
        appliedCount: published.appliedFindingCount,
      });
      await queryClient.invalidateQueries({ queryKey: QUERY_KEY.voiceAgent });
      await queryClient.invalidateQueries({ queryKey: QUERY_KEY.agentVersion });
      await queryClient.invalidateQueries({ queryKey: QUERY_KEY.optimisationReview });
    },
    async onError() {
      // A stale sequence. Refetch so the next attempt quotes what is live now, and do NOT
      // retry with the number that was just refused.
      setOutcome({ kind: "conflict", label: "", appliedCount: 0 });
      await queryClient.invalidateQueries({ queryKey: QUERY_KEY.voiceAgent });
      await queryClient.invalidateQueries({ queryKey: QUERY_KEY.agentVersion });
    },
  });

  const open = optimisationReviewId === null ? undefined : current.data;
  // plan-015 DRF: whether the review's version is still a draft (never published) — "In a draft" or "Published".
  const agentVersion = useQuery(agentVersionListQuery(gateway, props.voiceAgentId));

  // plan-032: nothing is drawn until it is known whether a review is to be restored, so the offer never flashes first.
  if (shown.kind === "follow" && optimisationReviewPage.isPending) return null;
  if (optimisationReviewId !== null && current.isPending) return null;

  // Review #37: nothing to review, nothing offered — nor to a template that is never reviewed. A review is always shown.
  const offered =
    quote.data === undefined || (quote.data.conversationCount > 0 && !isOptimisationReviewNotOffered(quote.data));
  if (open === undefined && outcome === null && (quote.data === undefined || !offered)) {
    return null;
  }

  // A review waiting, running, or done with findings to decide holds the panel; one that failed or found nothing is
  // said above the offer, which stays open.
  const holdsPanel =
    open !== undefined &&
    (open.state === "idle" || open.state === "running" || (open.state === "done" && open.finding.length > 0));

  return (
    <div className="c-optimisation">
      {open === undefined ? null : open.state === "idle" || open.state === "running" ? (
        <Banner
          done={false}
          title={
            open.state === "idle"
              ? portalText("operations", "review.waiting_to_start")
              : portalText("operations", "review.reading", {
                  count: open.conversationCount,
                  figure: open.conversationCount.toLocaleString(portalLocale() === "el" ? "el-GR" : "en-GB"),
                })
          }
          dek={heldTextOf(open)}
          action={<Icon glyph={GLYPH.hourglass} size="lg" />}
        />
      ) : open.state === "done" && open.finding.length > 0 ? (
        // Design `ai`: "Review complete" with what it found.
        <Banner
          done
          title={portalText("operations", "review_complete_5c0e1f3a")}
          dek={portalText("operations", "review.issues_found_read", { count: open.finding.length })}
          action={null}
        />
      ) : open.state === "done" ? (
        <p className="c-note" role="status" data-review-outcome="nothing_to_change">
          {portalText("operations", "review.nothing_to_change")} ·{" "}
          {reviewedCountText(open.conversationReadCount, open.conversationCount)}
        </p>
      ) : (
        <p className="c-note c-note-critical" role="status" data-review-outcome="failed">
          <strong>{portalText("operations", "review.did_not_finish")}</strong>{" "}
          {optimisationFailureText(open.failureReason)}
        </p>
      )}

      {outcome === null ? null : (
        <p
          className={outcome.kind === "conflict" ? "c-va-alert c-va-alert-critical" : "c-va-alert"}
          role="status"
        >
          <span className="c-va-alert-icon">
            <Icon
              glyph={outcome.kind === "conflict" ? GLYPH.warningCircle : GLYPH.checkCircle}
              size="lg"
            />
          </span>
          {outcome.kind === "conflict" ? (
            <span>
              {portalText("operations", "somebody_else_published_while_you_were_deciding_so_no_draf_396ec294")}</span>
          ) : (
            <span>
              {outcome.label} {portalText("operations", "is_saved_as_a_draft_with_b0ffb67b")}{" "}
              {outcome.appliedCount === 1 ? portalText("operations", "the_one_fix_40ffe328") : portalText("operations", "the_value_fixes_a54a0889", { v0: String(String(outcome.appliedCount)) })}{" "}
              {portalText("operations", "you_accepted_nothing_changes_on_live_calls_until_you_publi_7d22f316")}</span>
          )}
        </p>
      )}

      {open !== undefined && open.state === "done" && open.finding.length > 0 ? (
        <FindingsPane
          open={open}
          draftState={draftStateOf(open.publishedAgentVersionId, agentVersion.data)}
          decidePending={decide.isPending}
          publishPending={publish.isPending}
          onDecide={(optimisationFindingId, decision) => {
            decide.mutate({ optimisationFindingId, decision });
          }}
          onPublish={() => {
            publish.mutate();
          }}
          onEditByHand={props.onEditByHand}
          onAdvice={{
            edit: props.onEditByHand,
            questions: props.onOpenQuestions,
            channelBinding: props.onOpenChannelBindingTab,
          }}
          onSetAside={() => {
            setShown({ kind: "chosen", optimisationReviewId: null });
            setOutcome(null);
          }}
        />
      ) : null}

      {holdsPanel || !offered ? null : (
        <StartForm
          quote={quote.data}
          credit={credit.data}
          depth={depth}
          payer={payer}
          absorbedReason={absorbedReason}
          refusal={refusal}
          pending={start.isPending}
          onDepth={(next) => {
            setDepth(next);
          }}
          onAbsorbedReason={(next) => {
            setAbsorbedReason(next);
          }}
          onStart={() => {
            start.mutate();
          }}
        />
      )}
    </div>
  );
}

/** The amount a waiting or running review holds (DD32-6), or that wetalk pays for it. */
function heldTextOf(open: OptimisationReviewView): string {
  return open.billing.payer === "wetalk"
    ? portalText("operations", "review.held_by_wetalk")
    : portalText("operations", "review.held", { amount: formatMoney(open.billing.amount) });
}

/** The design's accent strip: a mark, a title, one sentence, and the act on the right. */
function Banner({
  done,
  title,
  dek = null,
  action,
}: {
  readonly done: boolean;
  readonly title: string;
  /** The line under the title; `null` is the offer's own sentence. */
  readonly dek?: string | null;
  readonly action: ReactNode;
}): ReactNode {
  usePortalTranslation("operations");
  return (
    <div className="c-optimisation-banner">
      <span className="c-optimisation-banner-mark">
        <Icon glyph={done ? GLYPH.checkCircle : GLYPH.sparkle} size="xl" />
      </span>
      <span className="c-optimisation-banner-copy">
        <span className="c-optimisation-banner-title">{title}</span>
        <span className="c-optimisation-banner-dek">
          {dek ?? portalText("operations", "it_goes_through_recent_calls_and_drafts_a_new_version_for_6261625b")}</span>
      </span>
      {action === null ? null : <span className="c-optimisation-banner-action">{action}</span>}
    </div>
  );
}

// ---------------------------------------------------------------------------------------
// Before anything is spent
// ---------------------------------------------------------------------------------------

function StartForm({
  quote,
  credit,
  depth,
  payer,
  absorbedReason,
  refusal,
  pending,
  onDepth,
  onAbsorbedReason,
  onStart,
}: {
  readonly quote: OptimisationReviewQuoteView | undefined;
  readonly credit: OptimisationReviewCreditView | undefined;
  readonly depth: OptimisationReviewDepthView;
  readonly payer: OptimisationReviewPayerView;
  readonly absorbedReason: string;
  readonly refusal: string | null;
  readonly pending: boolean;
  readonly onDepth: (next: OptimisationReviewDepthView) => void;
  readonly onAbsorbedReason: (next: string) => void;
  readonly onStart: () => void;
}): ReactNode {
  usePortalTranslation("operations");
  const [choosing, setChoosing] = useState(false);
  const chosen = quote?.option.find((one) => one.depth === depth);
  const blockedReason = chosen?.unavailableReason ?? null;
  const reasonUsable = isUsableAbsorbedReason(absorbedReason);
  const absorbing = payer === "wetalk";
  const runnable =
    quote !== undefined && chosen !== undefined && blockedReason === null && (!absorbing || reasonUsable);
  // plan-032 (OD-6): not open to this person yet — the offer is drawn, its button disabled, "Coming soon".
  const comingSoon = quote !== undefined && isOptimisationReviewComingSoon(quote);

  // The window's counts, which the design does not print: the run button's `title`.
  const countText =
    quote === undefined
      ? portalText("operations", "counting_the_calls_in_the_last_seven_days_c91c486f")
      : portalText("operations", "review.count_title", {
          count: quote.conversationCount,
          figure: quote.conversationCount.toLocaleString(portalLocale() === "el" ? "el-GR" : "en-IE"),
        });

  const confirmLabel =
    chosen === undefined
      ? portalText("operations", "reading_the_price_2dc1aba4")
      : `${depth === "ultra" ? portalText("operations", "start_ultra_review_56c90cd5") : portalText("operations", "start_review_d0bc5cdf")} · ${formatMoney(chosen.price)}${absorbing ? portalText("operations", "on_us_6430b51d") : ""}`;

  return (
    <>
      <Banner
        done={false}
        title={portalText("operations", "let_wetalk_review_the_calls_82383786")}
        action={
          choosing ? null : (
            <span className="c-optimisation-offer" title={countText}>
              {comingSoon ? <Tag tone="outline">{portalText("operations", "review.coming_soon")}</Tag> : null}
              <Button
                variant="primary"
                disabled={quote === undefined || comingSoon}
                onClick={() => {
                  setChoosing(true);
                }}
              >
                <Icon glyph={GLYPH.sparkle} size="md" />
                <span>{portalText("operations", "review_calls_9b2c8cad")}</span>
              </Button>
            </span>
          )
        }
      />

      {!choosing || comingSoon ? null : (
      <>
      <p className="c-optimisation-choose">{portalText("operations", "choose_how_deep_it_goes_b638eece")}</p>
      <div className="c-optimisation-depth-grid">
        {DEPTH_COPY.map((copy) => {
          const option = quote?.option.find((one) => one.depth === copy.depth);
          const unavailable = option?.unavailableReason ?? null;
          // plan-032 (OD-1): a depth not offered yet is marked "Coming later" in place of its price.
          const comingLater = unavailable === "not_available_yet";
          return (
            <label
              key={copy.depth}
              className="c-optimisation-depth"
              aria-disabled={unavailable === null ? undefined : true}
            >
              <input
                type="radio"
                name="optimisation-depth"
                value={copy.depth}
                checked={copy.depth === depth}
                disabled={unavailable !== null}
                onChange={() => {
                  onDepth(copy.depth);
                }}
              />
              <span className="c-optimisation-depth-body">
                <span className="c-optimisation-depth-head">
                  <span className="c-optimisation-depth-name">{copy.name}</span>
                  {comingLater ? (
                    <Tag tone="outline">{portalText("operations", "review.coming_later")}</Tag>
                  ) : (
                    <span className="c-optimisation-depth-price">
                      {option === undefined ? "—" : formatMoney(option.price)}
                    </span>
                  )}
                </span>
                <span className="c-optimisation-depth-dek">{copy.dek}</span>
                <span className="c-optimisation-point">
                  {copy.point.map((one) => (
                    <span key={one} className="c-optimisation-point-row">
                      <Icon glyph={GLYPH.check} size="sm" />
                      {one}
                    </span>
                  ))}
                </span>
                {unavailable === null || !isDepthOwnReason(unavailable) ? null : (
                  <span className="c-optimisation-unavailable">
                    <Icon glyph={GLYPH.warningCircle} size="md" />
                    {unavailableReasonText(unavailable)}
                  </span>
                )}
              </span>
            </label>
          );
        })}
      </div>

      <div className="c-optimisation-confirm">
        <span title={countText}>
          <Button
            variant="primary"
            disabled={!runnable || pending}
            onClick={() => {
              onStart();
            }}
          >
            <Icon glyph={depth === "ultra" ? GLYPH.waveform : GLYPH.sparkle} size="md" />
            <span>{pending ? portalText("operations", "starting_e5f58095") : confirmLabel}</span>
          </Button>
        </span>
        <Button
          variant="ghost"
          disabled={pending}
          onClick={() => {
            setChoosing(false);
          }}
        >
          {portalText("operations", "cancel_77dfd213")}</Button>
        <span className="c-optimisation-confirm-note">
          {absorbing ? portalText("operations", "wetalk_absorbs_it_nothing_is_taken_from_the_credit_3aac2321") : portalText("operations", "review.start_note")}
        </span>
      </div>

      {absorbing ? (
        <div className="c-optimisation-payer">
          <span className="c-detail-label">{portalText("operations", "who_pays_for_it_09a5f4db")}</span>
          <p className="c-optimisation-depth-dek">
            {portalText("operations", "wetalk_absorbs_it_you_are_acting_for_this_account_as_a_wet_77d0d8a9")}</p>
          <Field
            label={portalText("operations", "why_wetalk_is_absorbing_it_513886da")}
            description={portalText("operations", "at_least_twelve_characters_this_is_read_back_later_by_peop_03345371")}
            {...(absorbedReason.trim() === "" || reasonUsable
              ? {}
              : { error: portalText("operations", "that_is_too_short_to_be_a_reason_say_what_actually_went_wr_dfcaf99b") })}
          >
            {(control) => (
              <Textarea
                {...control}
                rows={2}
                placeholder={portalText("operations", "mispronounced_prices_after_our_tts_change_on_the_18th_c1e5e3fe")}
                value={absorbedReason}
                onChange={(event) => {
                  onAbsorbedReason(event.currentTarget.value);
                }}
              />
            )}
          </Field>
        </div>
      ) : null}

      </>
      )}

      {refusal === null ? null : (
        <p className="c-va-alert c-va-alert-critical" role="alert">
          <span className="c-va-alert-icon">
            <Icon glyph={GLYPH.warningCircle} size="lg" />
          </span>
          <span>{refusal}</span>
        </p>
      )}

      {/* Said before and while choosing (review 119): what stands in the way, money first. */}
      {blockedReason === null || chosen === undefined ? null : (
        <p className="c-optimisation-blocked" role="status">
          {blockedReason === "insufficient_credit"
            ? optimisationReviewCreditNoticeOf(chosen.price, credit)
            : unavailableReasonText(blockedReason)}
        </p>
      )}
    </>
  );
}

// ---------------------------------------------------------------------------------------
// What it found
// ---------------------------------------------------------------------------------------

/** The review's version as the panel tags it (plan-015 DRF): none yet, a draft, or published. */
type DraftState = { readonly kind: "none" } | { readonly kind: "draft" | "published"; readonly label: string | null };

export function draftStateOf(
  publishedAgentVersionId: string | null,
  agentVersion: readonly AgentVersionSummaryView[] | undefined,
): DraftState {
  if (publishedAgentVersionId === null) return { kind: "none" };
  const made = agentVersion?.find((one) => one.agentVersionId === publishedAgentVersionId);
  if (made === undefined) return { kind: "draft", label: null };
  return { kind: made.publishedAtText === null ? "draft" : "published", label: made.label };
}

/** Where "Change it yourself" can take the person; an absent one leaves the advice as words only. */
interface AdviceOpener {
  readonly edit: (() => void) | undefined;
  readonly questions: (() => void) | undefined;
  readonly channelBinding: (() => void) | undefined;
}

/** plan-032: findings by position (most conversations first), as the api orders them; older findings keep their place. */
export function findingsByPosition(finding: readonly OptimisationFindingView[]): readonly OptimisationFindingView[] {
  return [...finding].sort(
    (left, right) => (left.position ?? Number.MAX_SAFE_INTEGER) - (right.position ?? Number.MAX_SAFE_INTEGER),
  );
}

function FindingsPane({
  open,
  draftState,
  decidePending,
  publishPending,
  onDecide,
  onPublish,
  onSetAside,
  onEditByHand,
  onAdvice,
}: {
  readonly open: OptimisationReviewView;
  readonly draftState: DraftState;
  readonly decidePending: boolean;
  readonly publishPending: boolean;
  readonly onDecide: (
    optimisationFindingId: string,
    decision: OptimisationFindingDecisionView,
  ) => void;
  readonly onPublish: () => void;
  readonly onSetAside: () => void;
  readonly onEditByHand: (() => void) | undefined;
  readonly onAdvice: AdviceOpener;
}): ReactNode {
  usePortalTranslation("operations");
  // Advice is changed by hand and never decided (design §43.11): the tally counts the fixes a draft can carry.
  const decidable = open.finding.filter((one) => one.fixKind !== "advice");
  const undecided = decidable.filter((one) => one.decision === "pending").length;
  const rejected = decidable.filter((one) => one.decision === "rejected").length;
  const published = open.publishedAgentVersionId !== null;

  return (
    <div className="c-optimisation-panel">
      <div className="c-optimisation-head">
        <p className="c-empty-title">
          {open.finding.length === 1
            ? portalText("operations", "one_issue_worth_fixing_d8c609b6")
            : portalText("operations", "value_issues_worth_fixing_37e348c0", { v0: String(String(open.finding.length)) })}
        </p>
        <span className="c-optimisation-cost">
          {formatMoney(open.billing.amount)}
          {open.billing.payer === "wetalk" ? portalText("operations", "absorbed_by_wetalk_c51e242d") : portalText("operations", "of_credit_used_8d32d8eb")} ·{" "}
          {reviewedCountText(open.conversationReadCount, open.conversationCount)}
        </span>
      </div>

      {open.billing.payer === "wetalk" ? (
        <p className="c-optimisation-absorbed">
          <Icon glyph={GLYPH.shieldCheck} size="md" />
          {portalText("operations", "wetalk_paid_for_this_one_recorded_reason_4cd264e5")}{open.billing.absorbedReason}
        </p>
      ) : null}

      <p className="c-empty-dek">
        {portalText("operations", "accept_the_fixes_you_agree_with_and_reject_the_rest_nothin_a03f9314")}</p>

      <div className="c-optimisation-finding-set">
        {findingsByPosition(open.finding).map((one) => (
          <FindingRow
            key={one.optimisationFindingId}
            finding={one}
            pending={decidePending}
            locked={published}
            onAdvice={onAdvice}
            onDecide={(decision) => {
              onDecide(one.optimisationFindingId, decision);
            }}
          />
        ))}
      </div>

      <p className="c-optimisation-tally">
        {String(open.acceptedCount)} {portalText("operations", "accepted_ffde0248")}{String(rejected)} {portalText("operations", "rejected_bb194a7a")}{" "}
        {String(undecided)} {portalText("operations", "still_undecided_bcc42a4e")}</p>

      <div className="c-optimisation-actions">
        {published ? (
          <Tag tone="outline">
            {draftState.kind === "published" ? portalText("operations", "published_483bf207") : portalText("operations", "in_a_draft_d3ab4528")}
            {draftState.kind !== "none" && draftState.label !== null ? ` · ${draftState.label}` : ""}
          </Tag>
        ) : (
          <Button
            variant="primary"
            disabled={open.acceptedCount === 0 || publishPending}
            onClick={() => {
              onPublish();
            }}
          >
            <Icon glyph={GLYPH.gitBranch} size="md" />
            {publishPending
              ? portalText("operations", "creating_the_draft_31be82ea")
              : open.acceptedCount === 1
                ? portalText("operations", "create_draft_value_with_one_fix_619f9716", { v0: String(open.nextAgentVersionLabel) })
                : portalText("operations", "create_draft_value_with_value_fixes_abefbf5c", { v0: String(open.nextAgentVersionLabel), v1: String(String(open.acceptedCount)) })}
          </Button>
        )}
        {onEditByHand === undefined ? null : (
          <Button
            variant="secondary"
            onClick={() => {
              onEditByHand();
            }}
          >
            {portalText("operations", "review_by_hand_4f6a2c19")}
          </Button>
        )}
        <Button
          variant="ghost"
          onClick={() => {
            onSetAside();
          }}
        >
          {portalText("operations", "put_this_aside_043da74f")}</Button>
      </div>

      {open.acceptedCount === 0 && !published ? (
        <p className="c-note">
          {portalText("operations", "nothing_is_accepted_yet_so_there_is_no_draft_to_create_acc_5e79bba1")}</p>
      ) : null}

      <p className="c-optimisation-aside">
        {portalText("operations", "nothing_changes_on_live_calls_until_you_publish_it_9d41b7e2")}</p>
    </div>
  );
}

/** "Change it yourself": the screen's button, or none when the screen cannot be opened from here. */
function AdviceLink({ screen, onAdvice }: { readonly screen: OptimisationAdviceScreenView; readonly onAdvice: AdviceOpener }): ReactNode {
  usePortalTranslation("operations");
  const open = screen === null ? undefined : onAdvice[screen];
  return (
    <span className="c-optimisation-advice">
      <span>{portalText("operations", "review.advice.change_it_yourself")}</span>
      {screen === null || open === undefined ? null : (
        <Button
          variant="secondary"
          onClick={() => {
            open();
          }}
        >
          {screen === "edit"
            ? portalText("operations", "review.advice.open_edit")
            : screen === "questions"
              ? portalText("operations", "review.advice.open_questions")
              : portalText("operations", "review.advice.open_channels")}
        </Button>
      )}
    </span>
  );
}

/** The conversation lines a finding rests on — or why they cannot be shown (design §43.11, OD-4). */
function EvidenceLines({ evidenceLine }: { readonly evidenceLine: OptimisationFindingEvidenceLineView }): ReactNode {
  usePortalTranslation("operations");
  if (evidenceLine.state === "withheld") {
    return <p className="c-optimisation-evidence-note">{portalText("operations", "review.evidence.withheld")}</p>;
  }
  if (evidenceLine.state === "gone") {
    return <p className="c-optimisation-evidence-note">{portalText("operations", "review.evidence.gone")}</p>;
  }
  // One block per conversation, its lines in order.
  const byConversation = new Map<string, (typeof evidenceLine.line)[number][]>();
  for (const line of evidenceLine.line) {
    const held = byConversation.get(line.conversationId);
    if (held === undefined) byConversation.set(line.conversationId, [line]);
    else held.push(line);
  }
  return (
    <div className="c-optimisation-evidence">
      {[...byConversation].map(([conversationId, lines]) => (
        <div key={conversationId} className="c-optimisation-evidence-conversation">
          <span className="c-optimisation-evidence-when">{lines[0]?.startedText}</span>
          {lines.map((line) => (
            <p key={line.turnIndex} className="c-optimisation-evidence-line" data-speaker={line.speaker}>
              <span className="c-optimisation-evidence-speaker">
                {line.speaker === "caller"
                  ? portalText("operations", "review.evidence.caller")
                  : portalText("operations", "review.evidence.assistant")}
              </span>
              {line.text}
            </p>
          ))}
        </div>
      ))}
    </div>
  );
}

/**
 * One finding: the issue, the evidence with its affected-call count, the proposed fix, and
 * the decision.
 *
 * Three states rather than a switch, because "not yet decided" and "rejected" are different
 * facts and a two-state switch can only tell one of them. The affected count is drawn beside
 * the decision because it is the number the decision turns on.
 *
 * plan-032: an advice finding is changed by hand — "Change it yourself" and the screen to do it on, no Accept and no
 * Reject; "Show the conversations" opens the lines the finding rests on.
 */
function FindingRow({
  finding,
  pending,
  locked,
  onAdvice,
  onDecide,
}: {
  readonly finding: OptimisationFindingView;
  readonly pending: boolean;
  readonly locked: boolean;
  readonly onAdvice: AdviceOpener;
  readonly onDecide: (decision: OptimisationFindingDecisionView) => void;
}): ReactNode {
  usePortalTranslation("operations");
  const [showing, setShowing] = useState(false);
  const advice = finding.fixKind === "advice";
  return (
    <div className="c-optimisation-finding" data-decision={finding.decision} data-fix-kind={finding.fixKind ?? undefined}>
      <span className="c-optimisation-finding-mark">
        <Icon glyph={GLYPH.warningCircle} size="lg" />
      </span>
      <span>
        <span className="c-optimisation-finding-issue">{finding.issue}</span>
        <span className="c-optimisation-finding-evidence">{finding.evidence}</span>
        <span className="c-optimisation-finding-fix">{portalText("operations", "fix_3b61f527")}{finding.proposedFixText}</span>
        {advice ? <AdviceLink screen={optimisationAdviceScreenOf(finding.adviceTarget)} onAdvice={onAdvice} /> : null}
        <span className="c-optimisation-evidence-toggle">
          <Button
            variant="ghost"
            aria-expanded={showing}
            onClick={() => {
              setShowing((now) => !now);
            }}
          >
            <Icon glyph={showing ? GLYPH.caretDown : GLYPH.caretRight} size="sm" />
            {showing
              ? portalText("operations", "review.evidence.hide")
              : portalText("operations", "review.evidence.show")}
          </Button>
        </span>
        {showing ? <EvidenceLines evidenceLine={finding.evidenceLine} /> : null}
      </span>
      <span className="c-optimisation-finding-decide">
        <span className="c-optimisation-finding-count">{finding.affectedText}</span>
        {advice ? null : locked ? (
          <Tag status={finding.decision === "accepted" ? "positive" : "muted"}>
            {finding.decision === "accepted" ? portalText("operations", "applied_7e3c5b10") : portalText("operations", "not_applied_168145c6")}
          </Tag>
        ) : (
          <>
            <Button
              variant={finding.decision === "accepted" ? "primary" : "secondary"}
              disabled={pending}
              onClick={() => {
                onDecide(finding.decision === "accepted" ? "pending" : "accepted");
              }}
            >
              {finding.decision === "accepted" ? portalText("operations", "accepted_61a0572c") : portalText("operations", "accept_bb54db51")}
            </Button>
            <Button
              variant={finding.decision === "rejected" ? "primary" : "ghost"}
              disabled={pending}
              onClick={() => {
                onDecide(finding.decision === "rejected" ? "pending" : "rejected");
              }}
            >
              {finding.decision === "rejected" ? portalText("operations", "rejected_27eeb7a2") : portalText("operations", "reject_2b03b592")}
            </Button>
          </>
        )}
      </span>
    </div>
  );
}
